import type { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { X } from 'lucide-react';
import { LanguageToggle } from '../LanguageToggle';
import { ThemeToggle } from '../PageWrapper';
import type { EliteFeatureFlags } from '../../lib/elitePilot';
import { useEscapeKey } from '../../hooks/useEscapeKey';

export type GradingOnlySettingsSheetProps = {
  open: boolean;
  onClose: () => void;
  isDarkMode: boolean;
  setIsDarkMode: (v: boolean) => void;
  scanStudentMode: 'single' | 'batch';
  eliteFlags: EliteFeatureFlags;
  setEliteFlags: React.Dispatch<React.SetStateAction<EliteFeatureFlags>>;
  onRetryClassroomSync?: () => void;
  syncStatus: 'idle' | 'ok' | 'error';
  isOnline: boolean;
};

export function GradingOnlySettingsSheet(
  props: GradingOnlySettingsSheetProps
): ReactElement | null {
  const { t } = useTranslation();
  const {
    open,
    onClose,
    isDarkMode,
    setIsDarkMode,
    scanStudentMode,
    eliteFlags,
    setEliteFlags,
    onRetryClassroomSync,
    syncStatus,
    isOnline,
  } = props;
  useEscapeKey(open, onClose);
  if (!open) return null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="dg-grading-settings-title"
      className="fixed inset-0 z-[120] flex items-end justify-center sm:items-center"
    >
      <button
        type="button"
        className="absolute inset-0 bg-slate-950/50 backdrop-blur-sm"
        aria-label={t('gradingOnly.settings.close')}
        onClick={onClose}
      />
      <div className="relative z-[1] flex max-h-[min(85dvh,32rem)] w-full max-w-md flex-col overflow-hidden rounded-t-2xl border border-dg-border-subtle bg-dg-bg-surface shadow-2xl dark:border-slate-700 dark:bg-slate-900 sm:rounded-2xl">
        <div className="flex shrink-0 items-center justify-between gap-2 border-b border-dg-border px-4 py-3 dark:border-slate-700">
          <h2
            id="dg-grading-settings-title"
            className="text-dg-section font-semibold text-dg-fg-primary dark:text-slate-50"
          >
            {t('gradingOnly.settings.title')}
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="inline-flex h-11 w-11 items-center justify-center rounded-dg-control text-dg-fg-muted hover:bg-dg-interaction-hover dark:hover:bg-slate-800"
            aria-label={t('gradingOnly.settings.close')}
          >
            <X className="h-5 w-5" aria-hidden />
          </button>
        </div>
        <div className="min-h-0 flex-1 space-y-4 overflow-hidden p-4">
          <div className="flex flex-wrap items-center gap-3">
            <LanguageToggle />
            <ThemeToggle isDarkMode={isDarkMode} setIsDarkMode={setIsDarkMode} />
          </div>
          <p className="text-dg-secondary text-dg-fg-muted dark:text-slate-400">
            {scanStudentMode === 'batch'
              ? t('gradingOnly.settings.scanModeBatch')
              : t('gradingOnly.settings.scanModeSingle')}
          </p>
          {syncStatus === 'error' && isOnline && onRetryClassroomSync ? (
            <button
              type="button"
              onClick={onRetryClassroomSync}
              className="dg-btn-secondary w-full"
            >
              {t('gradingOnly.settings.retryClassroom')}
            </button>
          ) : null}
          <label className="flex items-center gap-2 text-dg-body text-dg-fg-primary dark:text-slate-100">
            <input
              type="checkbox"
              checked={eliteFlags.cameraFirstLaunch}
              onChange={(e) =>
                setEliteFlags((f) => ({ ...f, cameraFirstLaunch: e.target.checked }))
              }
              className="h-4 w-4 rounded border-dg-border"
            />
            {t('gradingOnly.settings.cameraFirst')}
          </label>
        </div>
      </div>
    </div>
  );
}
